import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { PhotoTile } from "@/components/photos/PhotoTile";
import { Skeleton } from "@/components/ui/skeleton";
import type { Photo } from "@/schemas/photos";

const MIN_TILE = 160;
const GAP = 8;
const SKELETON_TILES = Array.from({ length: 24 }, (_, i) => `tile-${i}`);
/** Ask for the next page this many rows before the end. */
const LOAD_AHEAD_ROWS = 6;

function columnsFor(width: number): number {
  return Math.max(2, Math.floor((width + GAP) / (MIN_TILE + GAP)));
}

interface PhotoGridProps {
  photos: Photo[];
  loading: boolean;
  hasNext: boolean;
  fetchingNext: boolean;
  onLoadMore: () => void;
  onOpen: (index: number) => void;
  isSelected: (photoId: string) => boolean;
  selecting: boolean;
  onSelect: (photoId: string, mode: "toggle" | "range") => void;
}

/**
 * Only the rows near the viewport are in the page, so 2,680 photos scroll like 50. The
 * scroller is the layout's `<main>`, and the grid stays mounted behind the viewer, which
 * is what brings a reader back to the same place.
 */
export function PhotoGrid({
  photos,
  loading,
  hasNext,
  fetchingNext,
  onLoadMore,
  onOpen,
  isSelected,
  selecting,
  onSelect,
}: PhotoGridProps) {
  const gridRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [offsetTop, setOffsetTop] = useState(0);

  useLayoutEffect(() => {
    const grid = gridRef.current;
    const scroller = grid?.closest("main");
    if (!grid || !scroller) return;
    const measure = () => {
      setWidth(grid.clientWidth);
      setOffsetTop(
        grid.getBoundingClientRect().top -
          scroller.getBoundingClientRect().top +
          scroller.scrollTop,
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(grid);
    observer.observe(scroller);
    return () => observer.disconnect();
  }, []);

  const columns = columnsFor(width);
  const tile = width > 0 ? (width - GAP * (columns - 1)) / columns : MIN_TILE;
  const rowCount = Math.ceil(photos.length / columns);

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => gridRef.current?.closest("main") ?? null,
    estimateSize: () => tile + GAP,
    overscan: 4,
    scrollMargin: offsetTop,
  });

  // Row height follows the width, and the virtualizer caches sizes until told otherwise.
  const measuredTile = useRef(tile);
  useEffect(() => {
    if (measuredTile.current === tile) return;
    measuredTile.current = tile;
    virtualizer.measure();
  }, [virtualizer, tile]);

  const rows = virtualizer.getVirtualItems();
  const lastRow = rows[rows.length - 1]?.index ?? 0;

  useEffect(() => {
    if (hasNext && !fetchingNext && rowCount > 0 && lastRow >= rowCount - LOAD_AHEAD_ROWS) {
      onLoadMore();
    }
  }, [hasNext, fetchingNext, rowCount, lastRow, onLoadMore]);

  if (loading) {
    return (
      <div ref={gridRef} className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-2">
        {SKELETON_TILES.map((key) => (
          <Skeleton key={key} className="aspect-square rounded-md" />
        ))}
      </div>
    );
  }

  return (
    <div ref={gridRef} data-testid="photo-grid" className="relative w-full">
      <div style={{ height: virtualizer.getTotalSize() }} />
      {rows.map((row) => {
        const first = row.index * columns;
        return (
          <div
            key={row.key}
            className="absolute inset-x-0 top-0 grid"
            style={{
              gap: GAP,
              gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
              transform: `translateY(${row.start - virtualizer.options.scrollMargin}px)`,
            }}
          >
            {photos.slice(first, first + columns).map((photo, offset) => (
              <PhotoTile
                key={photo.id}
                photo={photo}
                index={first + offset}
                selected={isSelected(photo.id)}
                selecting={selecting}
                onOpen={onOpen}
                onSelect={onSelect}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}
